import RecaptchaWidget from '@components/App/RecaptchaWidget'
import { AuthorAvatar } from '@components/ArtBoard/Threads/components/AuthorAvatar'
import { type EditorValue, isEmptyValue } from '@octopus-do/octopus-editor'
import type React from 'react'
import { useEffect, useState } from 'react'

import { useGuestName } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { Recipient } from '@/types'
import type { Payload } from '../../types'
import css from './CommentsForm.module.scss'
import { GuestName } from './components/GuestName'
import { Placeholder } from './components/Placeholder'
import { Textarea } from './components/Textarea'
import { parseCommentDraft, parseRecipients } from './utils'

interface Props {
  author: Recipient | null
  localStorageKey: string
  showForm: boolean
  focus?: boolean
  asGuest?: boolean
  onCommit: (payload: Payload) => void
  editingCommentId: string | null
}

const CommentsForm: React.FC<Props> = ({
  localStorageKey,
  showForm,
  focus,
  asGuest = false,
  onCommit,
  editingCommentId,
  author,
}) => {
  const storageKey = `${localStorageKey}_new_comment`

  const [active, setActive] = useState(false)
  const [error, setError] = useState(false)
  const [value, setValue] = useState<EditorValue>([])
  const [loading, setLoading] = useState(false)
  // prefilled from storage rather than asked again — the guest is never made to
  // retype it, but the name stays theirs to correct on any later comment
  const { commitGuestName, guestName, setGuestName } = useGuestName()
  const artboardDisabled = useArtBoardDisabled()

  useEffect(() => {
    // todo: remove useEffect, split ls usage into separate hook
    const draft = localStorage.getItem(storageKey)

    const parsed = parseCommentDraft(draft)

    setValue(parsed)
    setActive(!isEmptyValue(parsed))
  }, [])

  useEffect(() => {
    setActive(!!focus)
  }, [focus])

  const onCancel = () => {
    setError(false)
    setLoading(false)

    setActive(false)

    setValue([])

    localStorage.removeItem(storageKey)
  }

  const onSubmit = async () => {
    const content = localStorage.getItem(storageKey)

    if (!content) {
      return
    }

    setError(false)
    setLoading(true)

    try {
      // committed before the request so the comment carries exactly the name
      // that was stored, whitespace-only included: both end up as ''
      const guestAuthor = asGuest ? commitGuestName() : ''

      await onCommit({
        content,
        recipients: parseRecipients(content),
        ...(asGuest ? { author: guestAuthor } : {}),
      })

      setLoading(false)
      setActive(false)
      setValue([])
      localStorage.removeItem(storageKey)
    } catch {
      setError(true)
      setLoading(false)
    }
  }

  const onUpdate = (next: EditorValue) => {
    if (isEmptyValue(next)) {
      localStorage.removeItem(storageKey)
      return
    }

    localStorage.setItem(storageKey, JSON.stringify(next))
    setValue(next)
  }

  if (!showForm) {
    return null
  }

  return (
    <div className={css.form}>
      <div className={css.avatar}>
        <AuthorAvatar author={author} size="md" />
      </div>

      <Placeholder
        active={active}
        onClick={() => setActive(true)}
        disabled={!!editingCommentId || artboardDisabled}
      />

      {active && (
        <RecaptchaWidget>
          <div className={css.body}>
            {asGuest && <GuestName value={guestName} onChange={setGuestName} />}

            <Textarea
              error={error}
              value={value}
              loading={loading}
              readonly={!showForm}
              onCancel={onCancel}
              onSubmit={onSubmit}
              onUpdate={onUpdate}
              submitButtonText="Add"
            />
          </div>
        </RecaptchaWidget>
      )}
    </div>
  )
}

export default CommentsForm
